import type { WorkoutSessionDetails } from '../dto/WorkoutSessionDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { WorkoutPersistenceTransactions } from '../ports/WorkoutPersistenceTransactions';
import type { WorkoutRepository } from '../ports/WorkoutRepository';
import { findCatalogExercise } from '../../domain/workout/ExerciseCatalog';
import type { WorkoutSessionStatus } from '../../domain/workout/enums';
import type { ExerciseLog } from '../../domain/workout/ExerciseLog';
import type { WorkoutSession } from '../../domain/workout/WorkoutSession';
import { fromExerciseLogData } from '../../domain/workout/WorkoutContent';
import {
  deriveSessionStatus,
  replaceExerciseLog,
  transitionExerciseLog,
  type ExerciseTransition,
} from '../../domain/workout/WorkoutTransitions';
import type { ExerciseLogId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

export class UpdateExerciseStatus {
  public constructor(
    private readonly workouts: WorkoutRepository,
    private readonly transactions: WorkoutPersistenceTransactions,
    private readonly clock: Clock,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
    exerciseId: ExerciseLogId,
    transition: ExerciseTransition,
    options?: { readonly userWeight?: number; readonly weightUnit?: 'kg' | 'lb' },
  ): Promise<Result<WorkoutSessionDetails, RoutineError>> {
    try {
      const session = await this.workouts.findByLogicalDate(profileId, logicalDate);
      if (!session) {
        return failure(new RoutineError('workout_not_found', 'Séance de sport introuvable.'));
      }
      const exercises = await this.transactions.listExercises(session.id);
      const current = exercises.find((exercise) => exercise.id === exerciseId);
      if (!current || current.profileId !== profileId) {
        return failure(new RoutineError('exercise_not_found', 'Exercice introuvable.'));
      }

      const timestamp = this.clock.now().toISOString();
      const updatedExercise = transitionExerciseLog(current, transition, timestamp, options);
      return this.commit(session, exercises, updatedExercise, timestamp);
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }

  public async replace(
    profileId: ProfileId,
    logicalDate: string,
    exerciseId: ExerciseLogId,
  ): Promise<Result<WorkoutSessionDetails, RoutineError>> {
    try {
      const session = await this.workouts.findByLogicalDate(profileId, logicalDate);
      if (!session) {
        return failure(new RoutineError('workout_not_found', 'Séance de sport introuvable.'));
      }
      const exercises = await this.transactions.listExercises(session.id);
      const current = exercises.find((exercise) => exercise.id === exerciseId);
      if (!current || current.profileId !== profileId) {
        return failure(new RoutineError('exercise_not_found', 'Exercice introuvable.'));
      }
      const content = fromExerciseLogData(current.data);
      const replacementId = content?.replacementCatalogId;
      const replacement = replacementId ? findCatalogExercise(replacementId) : undefined;
      if (!replacement) {
        return failure(
          new RoutineError('exercise_not_replaceable', 'Cet exercice n’a pas d’alternative.'),
        );
      }

      const timestamp = this.clock.now().toISOString();
      const updatedExercise = replaceExerciseLog(current, replacement, timestamp);
      return this.commit(session, exercises, updatedExercise, timestamp);
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }

  private async commit(
    session: WorkoutSession,
    exercises: readonly ExerciseLog[],
    updatedExercise: ExerciseLog,
    timestamp: string,
  ): Promise<Result<WorkoutSessionDetails, RoutineError>> {
    const updatedExercises = exercises.map((exercise) =>
      exercise.id === updatedExercise.id ? updatedExercise : exercise,
    );
    const updatedSession = {
      ...session,
      status: deriveSessionStatus(session.status as WorkoutSessionStatus, updatedExercises),
      updatedAt: timestamp,
    };
    await this.transactions.updateSessionAndExercise(updatedSession, updatedExercise);
    return success({ session: updatedSession, exercises: updatedExercises });
  }
}
